import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Stamp, Check, Lock, Sparkles, MapPin, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import CustomerTabBar from "../../components/CustomerTabBar";

function ProgressRing({ value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="relative h-12 w-12 shrink-0">
      <svg viewBox="0 0 36 36" className="h-12 w-12 -rotate-90">
        <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
        <circle cx="18" cy="18" r="15" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * 94.2} 94.2`} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">{value}/{total}</span>
    </div>
  );
}

export default function Passaporto() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null); // passport detail
  const [busy, setBusy] = useState("");

  const loadList = () => api.get("/passports").then((r) => setList(r.data || [])).catch(() => setList([]));
  useEffect(() => { loadList(); }, []);

  const openPassport = async (id) => {
    try { const { data } = await api.get(`/passports/${id}`); setOpen(data); }
    catch (e) { toast.error("Impossibile aprire il passaporto"); }
  };

  const claim = async (stageId) => {
    if (!open) return;
    setBusy(stageId);
    try {
      const { data } = await api.post(`/passports/${open.id}/stages/${stageId}/claim`);
      const xp = data?.award?.xp || 0;
      toast.success(`Timbro ottenuto! ${xp ? `+${xp} XP` : ""}`);
      if (data?.completed && data?.completion_reward) toast.success("🏆 Passaporto completato!");
      await openPassport(open.id); loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Requisito non ancora soddisfatto");
    }
    setBusy("");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-24 animate-in fade-in duration-300" data-testid="passaporto-page">
      <header className="sticky top-0 z-10 bg-[#0A0A0C]/90 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button data-testid="passaporto-back" onClick={() => (open ? setOpen(null) : navigate(-1))} className="text-white/80"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-['Unbounded'] font-bold text-lg flex items-center gap-2"><Stamp className="h-5 w-5 text-[#00E676]" /> Passaporto Fuma</h1>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {!open && (
          <>
            <p className="text-[#7DF9FF]/90 italic text-sm mb-5">"Non limitarti a vivere il territorio. Collezionalo."</p>
            {list === null && <div className="text-white/50">Caricamento…</div>}
            {list && list.length === 0 && <div className="text-white/50">Nessun passaporto attivo al momento.</div>}
            <div className="space-y-3">
              {(list || []).map((p) => (
                <button key={p.id} data-testid={`passport-${p.key}`} onClick={() => openPassport(p.id)}
                  className="w-full text-left rounded-3xl p-4 border transition-colors flex items-center gap-4 active:scale-[0.99]"
                  style={{ borderColor: `${p.color}55`, background: `linear-gradient(135deg, ${p.color}18, #131316)` }}>
                  <ProgressRing value={p.stamped} total={p.total_stages} color={p.color} />
                  <div className="flex-1 min-w-0">
                    <div className="font-['Unbounded'] font-bold text-base flex items-center gap-2">
                      {p.title}{p.completed && <Trophy className="h-4 w-4 text-[#FFD700]" />}
                    </div>
                    <div className="text-xs text-white/55 mt-0.5">{p.subtitle}</div>
                  </div>
                  <span className="text-xs font-semibold" style={{ color: p.color }}>{p.completed ? "Completo" : "Apri →"}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {open && (
          <div data-testid="passport-detail">
            <div className="rounded-3xl p-5 mb-4 border" style={{ borderColor: `${open.color}55`, background: `linear-gradient(135deg, ${open.color}18, #131316)` }}>
              <div className="font-['Unbounded'] font-black text-2xl">{open.title}</div>
              <div className="text-sm text-white/60 mt-1">{open.subtitle}</div>
              {open.completed && <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold bg-[#FFD700]/15 text-[#FFD700] px-3 py-1 rounded-full"><Trophy className="h-3.5 w-3.5" /> Passaporto completato</div>}
            </div>
            <div className="grid grid-cols-1 gap-2.5">
              {open.stages.map((s) => (
                <div key={s.id} data-testid={`stage-${s.id}`}
                  className={`rounded-2xl p-4 border flex items-center gap-3 ${s.stamped ? "border-[#00E676]/40 bg-[#00E676]/8" : "border-white/10 bg-[#131316]"}`}>
                  <div className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 border-2 ${s.stamped ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10" : "border-white/15 text-white/40"}`}>
                    {s.stamped ? <Check className="h-5 w-5" /> : <Lock className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{s.title}</div>
                    <div className="text-xs text-white/50">{s.desc}</div>
                    <div className="text-[11px] text-[#7DF9FF] mt-1 flex items-center gap-2">
                      {s.reward?.xp ? <span className="flex items-center gap-0.5"><Sparkles className="h-3 w-3" /> +{s.reward.xp} XP</span> : null}
                      {s.reward?.badge ? <span className="flex items-center gap-0.5"><Trophy className="h-3 w-3 text-[#FFD700]" /> {s.reward.badge}</span> : null}
                    </div>
                  </div>
                  {s.stamped ? (
                    <span className="text-[10px] font-bold text-[#00E676] uppercase shrink-0">Timbrato</span>
                  ) : s.claimable ? (
                    <button data-testid={`claim-${s.id}`} disabled={busy === s.id} onClick={() => claim(s.id)}
                      className="text-xs font-semibold bg-[#00E676] text-black rounded-full px-3 py-1.5 shrink-0 disabled:opacity-60">
                      {busy === s.id ? "…" : "Timbra"}
                    </button>
                  ) : (
                    <span className="text-[10px] text-white/40 shrink-0 flex items-center gap-1"><MapPin className="h-3 w-3" /> Da sbloccare</span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-white/40 mt-4">I timbri si sbloccano completando azioni reali nel territorio. La verifica avviene lato server.</p>
          </div>
        )}
      </main>
      <CustomerTabBar />
    </div>
  );
}
